import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Tooltip
} from "@mui/material";
import { Download, Visibility } from "@mui/icons-material";
import { useOutletContext } from "react-router-dom";
import WaitSpinner from "../../../components/wait-spinner";
import { CampaignOutletContextValue } from "../form";
import {
    GetContractTemplatesQueryQuery,
    useGetContractTemplatesQueryQuery,
    usePdfDownloadMutation,
} from "../../../generated/graphql";
import ContractDocument, { EditLiveToggle } from "../../../components/contract/ContractDocument";
import { campaignTokenContext } from "../../../components/contract/tokenRenderer";
import { FileInputFile } from "../../../components/file-input";
import FileSaver from "file-saver";
import { useToast } from "../../../contexts/toast";
import { progressToast } from "../../../utils/progress-toast";
import ContractVersionHistory from "./ContractVersionHistory";

// Temp id for objects not yet persisted; the save pipeline converts it to
// creation_id (same convention as FieldTable addRow / FileInput uploads).
const tempId = (): string => Math.floor(Date.now()) + '-' + Math.floor(Math.random() * 1000000);

type TemplateGroup = NonNullable<GetContractTemplatesQueryQuery['contractTemplates']>[number];

// A template file copied into the campaign: same client-side preview data,
// plus clone_id so the backend copies the stored file at save time.
type ClonedFile = FileInputFile & { clone_id: string };

// Deep-copies a predefined template group into campaign form values: fresh
// client ids throughout, and template files become clone references that the
// backend resolves at save time (design.md §4).
const cloneTemplateGroup = (template: TemplateGroup, startSort: number) =>
    (template.sections ?? []).map((tsec, i) => ({
        id: tempId(),
        title: tsec.title ?? null,
        show_title: tsec.show_title ?? false,
        new_page: tsec.new_page ?? false,
        sort: startSort + i,
        blocks: (tsec.blocks ?? []).map((tblock, j) => ({
            id: tempId(),
            type: tblock.type ?? 'text',
            title: tblock.title ?? null,
            content: tblock.content ?? null,
            sort: j,
            files: (tblock.files ?? []).filter(Boolean).map<ClonedFile>(f => ({
                id: tempId(),
                url: f.url ?? undefined,
                thumbnail_url: f.thumbnail_url ?? undefined,
                file_type: f.file_type ?? undefined,
                distributor: f.distributor ? { connect: f.distributor.id } : undefined,
                clone_id: f.id,
            })),
        })),
    }));

const ContractPane = () => {
    const { values, setValue, loading, readOnly, setTab, isDirty } = useOutletContext<CampaignOutletContextValue>();
    const { data: templatesData, loading: templatesLoading } = useGetContractTemplatesQueryQuery();
    const { toast } = useToast();
    const [pdfDownloadFunc] = usePdfDownloadMutation();
    const [mode, setMode] = useState<'edit' | 'live'>('edit');
    // 'contract' shows the editor; 'history' shows the version history (5.1/5.2)
    const [view, setView] = useState<'contract' | 'history'>('contract');

    useEffect(() => {
        setTab('contract');
    }, []);

    const sections = (values?.contractSections || []).filter(Boolean);
    const templates = (templatesData?.contractTemplates || []).filter(Boolean);

    // Applying a predefined group deep-copies its sections into the campaign
    // form values; the backend copies the template files at save time.
    const applyTemplate = (template: TemplateGroup) => {
        const current = (values?.contractSections || []).filter(Boolean);
        setValue('contractSections', [...current, ...cloneTemplateGroup(template, current.length)]);
    };

    const onPreview = () => {
        const origin = window.serverUri || document.location.origin;
        window.open(origin + '/campaign-preview/' + values.job_reference + '/' + values.portfolio_hash + '/contract', '_blank');
    };

    const onPDF = useCallback(async () => {
        const t = progressToast(toast)('CS_CONTRACT_PDF');
        t.start('Generating PDF');
        try {
            const data = await pdfDownloadFunc({
                variables: {
                    host: 'preview',
                    url: '/campaign-preview/' + values.job_reference + '/' + values.portfolio_hash + '/contract',
                    outline: true,
                    expectText: values.job_reference,
                }
            });
            t.ok('Rendering file');
            const res = await fetch(data.data.pdfDownload.data);
            const blobContent = await res.blob();
            FileSaver.saveAs(blobContent, values.job_reference + '.pdf');
        } catch (e) {
            t.fail(e.message ? e.message : 'PDF generation failed');
        }
    }, [pdfDownloadFunc, toast, values.job_reference, values.portfolio_hash]);

    if (loading) {
        return <WaitSpinner variant="panel" />;
    }

    // Preview/PDF read the saved campaign, so gate both on unsaved changes and
    // on having any contract content (design §6).
    const docDisabled = isDirty || sections.length === 0;
    const pdfTooltip = isDirty ? 'Save before downloading' : 'No contract content';
    const previewTooltip = isDirty ? 'Save before previewing' : 'No contract content';

    if (view === 'history') {
        return (
            <div className="is-scrollable">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, my: 1, position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff' }}>
                    <Button size="small" onClick={() => setView('contract')}>
                        Back to contract
                    </Button>
                </Box>
                <ContractVersionHistory />
            </div>
        );
    }

    return (
        <div className="is-scrollable">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, my: 1, position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <EditLiveToggle mode={mode} onChange={setMode} />
                    <Button size="small" onClick={() => setView('history')}>
                        Version history
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={docDisabled ? previewTooltip : ''}>
                        <span>
                            <Button
                                size="small"
                                startIcon={<Visibility />}
                                disabled={docDisabled}
                                onClick={onPreview}
                            >
                                Preview
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={docDisabled ? pdfTooltip : ''}>
                        <span>
                            <Button
                                size="small"
                                startIcon={<Download />}
                                disabled={docDisabled}
                                onClick={onPDF}
                            >
                                Download PDF
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
            <ContractDocument
                value={{ sections }}
                onChange={(v) => setValue('contractSections', v.sections || [])}
                mode={mode}
                readOnly={readOnly}
                tokenContext={campaignTokenContext(values)}
                templates={templates}
                templatesLoading={templatesLoading}
                onApplyTemplate={applyTemplate}
                logoUrl={values.salesTeam?.logos?.[0]?.thumbnail_url ?? values.salesTeam?.logos?.[0]?.url ?? null}
                footerText={values.salesTeam?.document_footer ?? null}
                artwork={values.artworkFiles ?? null}
            />
        </div>
    );
};

export default ContractPane;
